/**
 * Copyright (C) Sitevision 2002-2026, all rights reserved
 */

import $ from '@sv/jquery';
import sv from '@sv/core';
import '../../util/portletUtil';
import { initSvAutoComplete } from './svAutoComplete';
import '../../vendor/pagination';
import {
  getPaginationElement,
  registerLegacySearchEventHandling,
  mergeLegacyPaginationSettings,
} from './legacyTemplates';
import { getPortlets } from './searchDomUtils';
import { updateSearchUrlParams } from './updateSearchUrlParams';

export const handleSearch = (selector, handleLegacy) => {
  const $portlets = getPortlets(selector);

  if ($portlets.length === 0) {
    return;
  }

  const updateSearchResult = ($portlet, query, operation) => {
    if (!query) {
      return false;
    }
    const $form = $portlet.find('form').first();
    const url = $form.data('fnBaseUrl') + query;
    const targetId = $form.data('fnResultContainer');
    const $target = $(targetId);
    updateSearchUrlParams(url, operation);
    sv.PortletUtil.doGetWithFade($target, url, updatePaging);
  };

  if (handleLegacy) {
    // Not slim, back-compat. Might have paging_script.
    // Legacy compatibility: expose callback names expected by old templates.
    registerLegacySearchEventHandling($portlets, updateSearchResult);
  }

  if (
    window.location.hash.indexOf('#query/') > -1 &&
    window.location.hash.length > 7
  ) {
    // Migrate legacy #query/... hash to query-param state.
    const queryIndex = window.location.hash.indexOf('#query/') + 7;
    const bookmarkedSearch = window.location.hash.substring(queryIndex);
    const decodedBookmarkedSearch = decodeURIComponent(bookmarkedSearch);

    if (decodedBookmarkedSearch) {
      $portlets.each((i, portlet) => {
        const $portlet = $(portlet);
        const $searchBox = $portlet.find('input[name=query]');
        if (!$searchBox.length) {
          return;
        }
        $searchBox.val(decodedBookmarkedSearch);
        updateSearchResult($portlet, bookmarkedSearch, 'query');
      });

      // Remove legacy hash after migration.
      history.replaceState(null, '', window.location.href.split('#')[0]);
    }
  }

  $portlets.each((i, portlet) => {
    const $portlet = $(portlet);
    const $autoComplete = $portlet.find('[data-fn-use-auto-complete]').first();
    if ($autoComplete.length) {
      const $searchBox = $autoComplete.find('input[name=query]');
      $searchBox.attr('autocomplete', 'off');
      initSvAutoComplete($searchBox[0], {
        source: $autoComplete.data('fnAutoCompleteUrl'),
      });
    }
  });

  $portlets.find('form').on('submit', (e) => {
    const $target = $(e.target);
    // Ensure '+' chars is encoded (might be treated as space char otherwise)
    const encodedQry = encodeURIComponent(e.target.elements.query.value);
    updateSearchResult($target.parent(), encodedQry, 'query');

    $target.find('[type=submit]').trigger('focus');
    return false;
  });

  updatePaging(selector);

  $portlets.on('click', '[data-sv-tracking-uri]', (e) => {
    const $target = $(e.target);
    const trackingUri = $target.data('sv-tracking-uri');
    $.ajax({ url: trackingUri, async: false });
  });
};

export const updatePaging = (selector) => {
  const p = new URLSearchParams(window.location.search);
  const $portlets = getPortlets(selector);

  $portlets.each((i, portlet) => {
    const $portlet = $(portlet);
    const $paging = getPaginationElement(portlet);

    mergeLegacyPaginationSettings($portlet, $paging);

    if ($paging.length) {
      $paging.pagination({
        hitCount: $paging.data('hitcount'),
        hitContainer: $paging.data('hitcontainer'),
        baseUrl: $paging.data('baseurl'),
        hitsPerPage: $paging.data('hitsperpage'),
        callback: (url) => updateSearchUrlParams(url, 'pagination'),
        startAtHit: p.get('startAtHit'),
        text: {
          prev: $paging.data('prevtext'),
          next: $paging.data('nexttext'),
          goToPage: $paging.data('gotopage'),
          paginationLabel: $paging.data('paginationlabel'),
          currentPage: $paging.data('currentpage'),
          morePages: $paging.data('morepages'),
        },
      });
    }
  });
};
